// ─── User Profile ────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  avatar_url: string | null;
  preferred_language: string;
  preferred_theme: string;
  is_active?: boolean;
  last_login_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
  preferred_language?: string;
  preferred_theme?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

// ─── Bookmarks ───────────────────────────────────────────────────────────────

export interface BookmarkResponse {
  id: string;
  thesis_id: string;
  thesis_title_en: string | null;
  thesis_title_fr: string | null;
  thesis_author_name: string;
  thesis_cover_image_url: string | null;
  thesis_avg_rating: number;
  thesis_degree_level: string;
  thesis_academic_year: string;
  thesis_defense_date: string;
  created_at: string;
}

export interface CreateBookmarkRequest {
  thesis_id: string;
}

// ─── Reading History & Progress ──────────────────────────────────────────────

export interface ReadingHistoryItem {
  thesis_id: string;
  thesis_title_en: string | null;
  thesis_title_fr: string | null;
  thesis_author_name: string;
  thesis_cover_image_url: string | null;
  thesis_degree_level: string;
  thesis_academic_year: string;
  last_page: number;
  total_pages: number;
  percentage: number;
  last_read_at: string;
}

export interface ReadingProgressResponse {
  thesis_id: string;
  last_page: number;
  total_pages: number;
  percentage: number;
  updated_at: string;
}

export interface UpdateReadingProgressRequest {
  last_page: number;
  total_pages: number;
}

export interface ClearHistoryResponse {
  message: string;
  deleted_count: number;
}

// ─── Reviews & Ratings ───────────────────────────────────────────────────────

export interface ReviewUserInfo {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

export interface ReviewResponse {
  id: string;
  thesis_id: string;
  user: ReviewUserInfo;
  rating: number;
  comment: string | null;
  is_approved: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminReviewResponse {
  id: string;
  thesis_id: string;
  thesis_title_en: string | null;
  thesis_title_fr: string | null;
  user: ReviewUserInfo;
  rating: number;
  comment: string | null;
  is_approved: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateReviewRequest {
  rating: number;
  comment?: string | null;
}

export interface UpdateReviewRequest {
  rating?: number;
  comment?: string | null;
}

export interface ModerateReviewRequest {
  is_approved: boolean;
}

export interface RatingSummary {
  avg_rating: number;
  rating_count: number;
  breakdown: RatingBreakdown;
}

export interface RatingBreakdown {
  five: number;
  four: number;
  three: number;
  two: number;
  one: number;
}

// ─── Notifications ───────────────────────────────────────────────────────────

export type NotificationType =
  | "new_thesis"
  | "new_review"
  | "author_cv_updated"
  | "author_claim_request"
  | "claim_approved"
  | "claim_rejected"
  | "system_announcement";

export interface NotificationResponse {
  id: string;
  user_id: string;
  notification_type: string;
  title_en: string;
  title_fr: string;
  body_en: string;
  body_fr: string;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

export interface NotificationListResponse {
  data: NotificationResponse[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  unread_count: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface MarkAllReadResponse {
  marked_count: number;
}

export interface SendAnnouncementRequest {
  title_en: string;
  title_fr: string;
  body_en: string;
  body_fr: string;
  link?: string | null;
}

export interface AnnouncementSentResponse {
  message: string;
  recipient_count: number;
}

// ─── Notification Preferences ────────────────────────────────────────────────

export interface NotificationPrefsPayload {
  new_thesis_inapp?: boolean;
  new_thesis_email?: boolean;
  author_updates_inapp?: boolean;
  author_updates_email?: boolean;
  weekly_digest?: boolean;
  subscribed_depts?: string[];
  subscribed_keywords?: string[];
  subscribed_authors?: string[];
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
}

export interface UpdatePreferencesRequest {
  preferred_language?: string;
  preferred_theme?: string;
  notification_prefs?: NotificationPrefsPayload;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  new_thesis_inapp: boolean;
  new_thesis_email: boolean;
  author_updates_inapp: boolean;
  author_updates_email: boolean;
  weekly_digest: boolean;
  subscribed_depts: string;
  subscribed_keywords: string;
  subscribed_authors: string;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
}

export interface FullPreferencesResponse {
  preferred_language: string;
  preferred_theme: string;
  /** Flattened notification preferences fields */
  id: string;
  user_id: string;
  new_thesis_inapp: boolean;
  new_thesis_email: boolean;
  author_updates_inapp: boolean;
  author_updates_email: boolean;
  weekly_digest: boolean;
  subscribed_depts: string;
  subscribed_keywords: string;
  subscribed_authors: string;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
}

// ─── Author Claims ───────────────────────────────────────────────────────────

export interface ClaimRequestResponse {
  message: string;
  status: string;
}

export interface AdminClaimAction {
  action: "approve" | "reject";
  reason?: string;
}

export interface ClaimListItem {
  author_id: string;
  author_first_name: string;
  author_last_name: string;
  author_email: string | null;
  claiming_user_id: string;
  claiming_user_first_name: string;
  claiming_user_last_name: string;
  claiming_user_email: string;
  claim_status: string;
  requested_at: string;
}

// ─── Dashboard Stats ─────────────────────────────────────────────────────────

export interface DashboardStats {
  total_bookmarks: number;
  theses_read_this_month: number;
  average_progress: number;
  total_reviews: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Get user initials for avatar fallback */
export function getUserInitials(user: {
  first_name: string;
  last_name: string;
}): string {
  const first = user.first_name.charAt(0).toUpperCase();
  const last = user.last_name.charAt(0).toUpperCase();
  return `${first}${last}`;
}

/** Get the localised notification title */
export function getNotificationTitle(
  notification: NotificationResponse,
  lang: string,
): string {
  return lang === "fr" ? notification.title_fr : notification.title_en;
}

/** Get the localised notification body */
export function getNotificationBody(
  notification: NotificationResponse,
  lang: string,
): string {
  return lang === "fr" ? notification.body_fr : notification.body_en;
}

/** Get a human-readable relative time string */
export function getRelativeTime(dateStr: string, lang: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (lang === "fr") {
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return date.toLocaleDateString("fr-FR");
  }

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString("en-US");
}

/** Notification type → icon name mapping (for Lucide icons) */
export const notificationTypeIcons: Record<string, string> = {
  new_thesis: "book-open",
  new_review: "star",
  author_cv_updated: "user",
  author_claim_request: "user-plus",
  claim_approved: "check-circle",
  claim_rejected: "x-circle",
  system_announcement: "megaphone",
};

/** Notification type → color mapping */
export const notificationTypeColors: Record<string, string> = {
  new_thesis: "text-blue-500",
  new_review: "text-amber-500",
  author_cv_updated: "text-green-500",
  author_claim_request: "text-purple-500",
  claim_approved: "text-green-500",
  claim_rejected: "text-red-500",
  system_announcement: "text-purple-500",
};

/** Get localised bookmark thesis title with fallback */
export function getBookmarkTitle(
  bookmark: BookmarkResponse,
  lang: string,
): { title: string; isFallback: boolean } {
  if (lang === "fr") {
    if (bookmark.thesis_title_fr)
      return { title: bookmark.thesis_title_fr, isFallback: false };
    if (bookmark.thesis_title_en)
      return { title: bookmark.thesis_title_en, isFallback: true };
  } else {
    if (bookmark.thesis_title_en)
      return { title: bookmark.thesis_title_en, isFallback: false };
    if (bookmark.thesis_title_fr)
      return { title: bookmark.thesis_title_fr, isFallback: true };
  }
  return { title: "Untitled", isFallback: true };
}

/** Get localised reading history item title with fallback */
export function getHistoryTitle(
  item: ReadingHistoryItem,
  lang: string,
): { title: string; isFallback: boolean } {
  if (lang === "fr") {
    if (item.thesis_title_fr)
      return { title: item.thesis_title_fr, isFallback: false };
    if (item.thesis_title_en)
      return { title: item.thesis_title_en, isFallback: true };
  } else {
    if (item.thesis_title_en)
      return { title: item.thesis_title_en, isFallback: false };
    if (item.thesis_title_fr)
      return { title: item.thesis_title_fr, isFallback: true };
  }
  return { title: "Untitled", isFallback: true };
}
